import {
  MIRRORED,
  SPRITE_KITS,
  SPRITE_LAYOUT,
  frameColumn,
  frameRow,
  type Facing,
  type SpriteKit,
  type SpriteLayout,
  type SpritePose,
} from "./spriteLayout";

/**
 * Player sprite sheets: loading with a timeout and per-sheet fallback, and frame blitting. A missing
 * or slow sheet never blocks the match — the renderer paints procedural figures for that kit until
 * (unless) the image arrives. Sheets are generated by tools/spriteGen.ts (see public/assets/README.md).
 */

export interface SpriteSet {
  layout: SpriteLayout;
  /** Loaded sheets; a kit absent here falls back to the procedural figure. */
  sheets: Partial<Record<SpriteKit, CanvasImageSource>>;
  /** Kits that failed to load (for diagnostics/tests). */
  failed: SpriteKit[];
}

export type ImageLoader = (url: string) => Promise<CanvasImageSource>;

export const DEFAULT_ASSET_BASE = "assets/";
export const LOAD_TIMEOUT_MS = 6000;

const browserLoader: ImageLoader = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load ${url}`));
    img.src = url;
  });

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`${label} timed out after ${ms} ms`)), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(t);
        reject(e instanceof Error ? e : new Error(String(e)));
      },
    );
  });
}

/** Load every sheet; never rejects. */
export async function loadSprites(base = DEFAULT_ASSET_BASE, loader: ImageLoader = browserLoader, timeoutMs = LOAD_TIMEOUT_MS): Promise<SpriteSet> {
  const set: SpriteSet = { layout: SPRITE_LAYOUT, sheets: {}, failed: [] };
  await Promise.all(
    SPRITE_KITS.map(async (kit) => {
      try {
        set.sheets[kit] = await withTimeout(loader(base + SPRITE_LAYOUT.files[kit]), timeoutMs, kit);
      } catch {
        set.failed.push(kit);
      }
    }),
  );
  return set;
}

let cached: Promise<SpriteSet> | null = null;

/** Shared, memoised load so the shell can warm the cache before the first match. */
export function spriteAssets(): Promise<SpriteSet> {
  if (!cached) cached = loadSprites();
  return cached;
}

/** Test/diagnostic hook: reset the memoised load. */
export function resetSpriteCache(): void {
  cached = null;
}

export interface FrameRef {
  kit: SpriteKit;
  pose: SpritePose;
  facing: Facing;
  variant: number;
}

/**
 * Blit one frame with its foot anchor at (x, y) and the figure `heightPx` tall. Returns false when the
 * sheet is unavailable so the caller can paint a fallback.
 */
export function drawSprite(ctx: CanvasRenderingContext2D, set: SpriteSet | null, ref: FrameRef, x: number, y: number, heightPx: number): boolean {
  const sheet = set?.sheets[ref.kit];
  if (!sheet) return false;
  const l = set!.layout;
  const { column, mirror } = MIRRORED[ref.facing];
  const sx = frameColumn(column) * l.frameWidth;
  const sy = frameRow(ref.pose, ref.variant) * l.frameHeight;
  const scale = heightPx / l.figureHeight;
  const dw = l.frameWidth * scale;
  const dh = l.frameHeight * scale;
  const dx = x - l.anchor.x * scale;
  const dy = y - l.anchor.y * scale;
  if (mirror) {
    ctx.save();
    ctx.translate(x, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(sheet, sx, sy, l.frameWidth, l.frameHeight, -(dw - (x - dx)) , dy, dw, dh);
    ctx.restore();
  } else {
    ctx.drawImage(sheet, sx, sy, l.frameWidth, l.frameHeight, dx, dy, dw, dh);
  }
  return true;
}
